import type {
  SqlCatalogColumn,
  SqlCatalogMetadataCache,
  SqlCatalogTable,
} from "@/components/query/sql/shared/sql-catalog-metadata";

export interface SqlCatalogCompletionRequest {
  catalog: SqlCatalogMetadataCache;
  cursorOffset: number;
  sql: string;
}

export type SqlCatalogCompletionKind =
  | "alias"
  | "column"
  | "datasource"
  | "keyword"
  | "schema"
  | "table";

export interface SqlCatalogCompletionItem {
  detail?: string;
  documentation?: string;
  insertText: string;
  kind: SqlCatalogCompletionKind;
  label: string;
  sortText: string;
}

interface SqlToken {
  end: number;
  quoted?: boolean;
  start: number;
  type: "comma" | "dot" | "identifier" | "operator" | "parenClose" | "parenOpen" | "semicolon";
  value: string;
}

interface SqlPathAtCursor {
  completedParts: string[];
  prefix: string;
  tokenStartOffset: number;
  trailingDot: boolean;
}

interface SqlTableReference {
  alias?: string;
  pathParts: string[];
  table?: SqlCatalogTable;
}

const SQL_KEYWORDS = [
  "SELECT",
  "FROM",
  "WHERE",
  "JOIN",
  "LEFT JOIN",
  "RIGHT JOIN",
  "INNER JOIN",
  "FULL OUTER JOIN",
  "CROSS JOIN",
  "ON",
  "ORDER BY",
  "GROUP BY",
  "HAVING",
  "QUALIFY",
  "LIMIT",
  "OFFSET",
  "AND",
  "OR",
  "NOT",
  "IN",
  "BETWEEN",
  "LIKE",
  "IS NULL",
  "IS NOT NULL",
  "AS",
  "DISTINCT",
  "COUNT",
  "SUM",
  "AVG",
  "MIN",
  "MAX",
];

const DATASOURCES_NAMESPACE = "datasources";

const CLAUSE_BOUNDARY_KEYWORDS = new Set([
  "where",
  "group",
  "having",
  "qualify",
  "order",
  "limit",
  "offset",
  "union",
  "except",
  "intersect",
  "minus",
  "sample",
]);

const JOIN_MODIFIER_KEYWORDS = new Set([
  "cross",
  "full",
  "inner",
  "join",
  "lateral",
  "left",
  "outer",
  "right",
]);

const NON_ALIAS_KEYWORDS = new Set([
  ...CLAUSE_BOUNDARY_KEYWORDS,
  ...JOIN_MODIFIER_KEYWORDS,
  "and",
  "as",
  "by",
  "on",
  "or",
  "using",
]);

const COLUMN_CONTEXT_KEYWORDS = new Set([
  "and",
  "by",
  "group",
  "having",
  "on",
  "order",
  "or",
  "qualify",
  "select",
  "where",
]);

const isIdentifierStart = (char: string) => /[A-Za-z_]/.test(char);

const isIdentifierPart = (char: string) => /[A-Za-z0-9_$]/.test(char);

const isIdentifierToken = (token: SqlToken | undefined): token is SqlToken =>
  token?.type === "identifier";

const normalizeIdentifier = (value: string) => value.toLowerCase();

const splitIdentifierPath = (value: string) => value.split(".").filter((part) => part.length > 0);

/** Chalk SQL identifier quoting: bare when simple, double-quoted (with `"`
 *  doubled) otherwise. Exported for callers that build table references
 *  outside the completion engine (e.g. mention hovercard prefill links). */
export const formatIdentifier = (value: string) =>
  /^[A-Za-z_][A-Za-z0-9_$]*$/.test(value) ? value : `"${value.replace(/"/g, '""')}"`;

const formatFullTablePath = (table: SqlCatalogTable) =>
  `${table.catalogName}.${table.dbSchemaName}.${table.tableName}`;

const formatQuotedFullTablePath = (table: SqlCatalogTable) => `"${formatFullTablePath(table)}"`;

const matchesPrefix = (value: string, prefix: string) =>
  prefix.length === 0 || value.toLowerCase().startsWith(prefix.toLowerCase());

const tokenIsIdentifierValue = (token: SqlToken | undefined, value: string) =>
  isIdentifierToken(token) && normalizeIdentifier(token.value) === value;

const isDatasourcesNamespace = (value: string) =>
  normalizeIdentifier(value) === DATASOURCES_NAMESPACE;

const tokenizeSql = (sql: string): SqlToken[] => {
  const tokens: SqlToken[] = [];
  let index = 0;

  while (index < sql.length) {
    const char = sql[index];

    if (/\s/.test(char)) {
      index += 1;
      continue;
    }

    if (char === "-" && sql[index + 1] === "-") {
      index += 2;
      while (index < sql.length && sql[index] !== "\n") {
        index += 1;
      }
      continue;
    }

    if (char === "/" && sql[index + 1] === "*") {
      index += 2;
      while (index < sql.length && !(sql[index] === "*" && sql[index + 1] === "/")) {
        index += 1;
      }
      index = Math.min(index + 2, sql.length);
      continue;
    }

    if (char === "'") {
      index += 1;
      while (index < sql.length) {
        if (sql[index] === "'" && sql[index + 1] === "'") {
          index += 2;
        } else if (sql[index] === "'") {
          index += 1;
          break;
        } else {
          index += 1;
        }
      }
      continue;
    }

    if (char === '"' || char === "`") {
      const quote = char;
      const start = index;
      let value = "";
      index += 1;
      while (index < sql.length) {
        if (sql[index] === quote && sql[index + 1] === quote) {
          value += quote;
          index += 2;
        } else if (sql[index] === quote) {
          index += 1;
          break;
        } else {
          value += sql[index];
          index += 1;
        }
      }
      tokens.push({ end: index, quoted: true, start, type: "identifier", value });
      continue;
    }

    if (char === "[") {
      const start = index;
      let value = "";
      index += 1;
      while (index < sql.length) {
        if (sql[index] === "]") {
          index += 1;
          break;
        }
        value += sql[index];
        index += 1;
      }
      tokens.push({ end: index, quoted: true, start, type: "identifier", value });
      continue;
    }

    if (isIdentifierStart(char)) {
      const start = index;
      index += 1;
      while (index < sql.length && isIdentifierPart(sql[index])) {
        index += 1;
      }
      tokens.push({ end: index, start, type: "identifier", value: sql.slice(start, index) });
      continue;
    }

    switch (char) {
      case ".":
        tokens.push({ end: index + 1, start: index, type: "dot", value: char });
        break;
      case ",":
        tokens.push({ end: index + 1, start: index, type: "comma", value: char });
        break;
      case "(":
        tokens.push({ end: index + 1, start: index, type: "parenOpen", value: char });
        break;
      case ")":
        tokens.push({ end: index + 1, start: index, type: "parenClose", value: char });
        break;
      case ";":
        tokens.push({ end: index + 1, start: index, type: "semicolon", value: char });
        break;
      default:
        tokens.push({ end: index + 1, start: index, type: "operator", value: char });
        break;
    }

    index += 1;
  }

  return tokens;
};

const getPathAtCursor = (tokens: SqlToken[], cursorOffset: number): SqlPathAtCursor | null => {
  const pathTokens: SqlToken[] = [];
  let tokenIndex = tokens.length - 1;

  if (tokens[tokenIndex]?.end !== cursorOffset) {
    return null;
  }

  while (tokenIndex >= 0) {
    const token = tokens[tokenIndex];
    if (token.type !== "identifier" && token.type !== "dot") {
      break;
    }
    if (pathTokens[0] != null && token.end !== pathTokens[0].start) {
      break;
    }

    pathTokens.unshift(token);
    tokenIndex -= 1;
  }

  if (pathTokens.length === 0) {
    return null;
  }

  const parts: string[] = [];
  let hasIdentifier = false;
  let expectingIdentifier = true;

  for (const token of pathTokens) {
    if (token.type === "identifier" && expectingIdentifier) {
      parts.push(...splitIdentifierPath(token.value));
      hasIdentifier = true;
      expectingIdentifier = false;
    } else if (token.type === "dot" && !expectingIdentifier) {
      expectingIdentifier = true;
    } else {
      return null;
    }
  }

  if (!hasIdentifier) {
    return null;
  }

  const trailingDot = pathTokens[pathTokens.length - 1]?.type === "dot";
  return {
    completedParts: trailingDot ? parts : parts.slice(0, -1),
    prefix: trailingDot ? "" : parts[parts.length - 1] ?? "",
    tokenStartOffset: pathTokens[0].start,
    trailingDot,
  };
};

const parseTablePath = (
  tokens: SqlToken[],
  startIndex: number,
): { nextIndex: number; pathParts: string[] } | null => {
  const pathParts: string[] = [];
  let index = startIndex;
  let expectingIdentifier = true;

  while (index < tokens.length) {
    const token = tokens[index];
    if (expectingIdentifier) {
      if (token.type !== "identifier") {
        break;
      }
      pathParts.push(...splitIdentifierPath(token.value));
      expectingIdentifier = false;
      index += 1;
      continue;
    }

    if (token.type !== "dot") {
      break;
    }

    expectingIdentifier = true;
    index += 1;
  }

  if (pathParts.length === 0 || expectingIdentifier) {
    return null;
  }

  return { nextIndex: index, pathParts };
};

const parseTableAlias = (
  tokens: SqlToken[],
  startIndex: number,
): { alias?: string; nextIndex: number } => {
  let index = startIndex;

  if (tokenIsIdentifierValue(tokens[index], "as")) {
    index += 1;
  }

  const aliasToken = tokens[index];
  if (!isIdentifierToken(aliasToken)) {
    return { nextIndex: startIndex };
  }

  const normalizedAlias = normalizeIdentifier(aliasToken.value);
  if (NON_ALIAS_KEYWORDS.has(normalizedAlias)) {
    return { nextIndex: startIndex };
  }

  return { alias: aliasToken.value, nextIndex: index + 1 };
};

const findPreviousTokenBeforeOffset = (tokens: SqlToken[], offset: number) => {
  for (let index = tokens.length - 1; index >= 0; index -= 1) {
    if (tokens[index].end <= offset) {
      return { index, token: tokens[index] };
    }
  }

  return null;
};

const isJoinToken = (token: SqlToken | undefined) =>
  tokenIsIdentifierValue(token, "join") || tokenIsIdentifierValue(token, "from");

const isInFromListAfterComma = (tokens: SqlToken[], commaIndex: number) => {
  for (let index = commaIndex - 1; index >= 0; index -= 1) {
    const token = tokens[index];
    if (token.type === "semicolon") {
      return false;
    }
    if (tokenIsIdentifierValue(token, "from")) {
      return true;
    }
    if (
      tokenIsIdentifierValue(token, "join") ||
      CLAUSE_BOUNDARY_KEYWORDS.has(normalizeIdentifier(token.value))
    ) {
      return false;
    }
  }

  return false;
};

const isTablePathContext = (
  tokensBeforeCursor: SqlToken[],
  pathAtCursor: SqlPathAtCursor | null,
) => {
  const previous = findPreviousTokenBeforeOffset(
    tokensBeforeCursor,
    pathAtCursor?.tokenStartOffset ?? Number.POSITIVE_INFINITY,
  );

  if (previous == null) {
    return false;
  }

  if (isJoinToken(previous.token)) {
    return true;
  }

  if (previous.token.type === "comma") {
    return isInFromListAfterComma(tokensBeforeCursor, previous.index);
  }

  return false;
};

const extractTableReferences = async (
  sql: string,
  catalog: SqlCatalogMetadataCache,
): Promise<SqlTableReference[]> => {
  const tokens = tokenizeSql(sql);
  const refs: SqlTableReference[] = [];
  let index = 0;

  while (index < tokens.length) {
    const token = tokens[index];
    const normalizedToken = isIdentifierToken(token) ? normalizeIdentifier(token.value) : "";

    if (normalizedToken !== "from" && normalizedToken !== "join") {
      index += 1;
      continue;
    }

    index += 1;

    while (
      tokens[index] != null &&
      JOIN_MODIFIER_KEYWORDS.has(normalizeIdentifier(tokens[index].value))
    ) {
      if (tokenIsIdentifierValue(tokens[index], "join")) {
        index += 1;
        break;
      }
      index += 1;
    }

    if (tokens[index]?.type === "parenOpen") {
      index += 1;
      continue;
    }

    const parsedPath = parseTablePath(tokens, index);
    if (parsedPath == null) {
      index += 1;
      continue;
    }

    index = parsedPath.nextIndex;
    const alias = parseTableAlias(tokens, index);
    index = alias.nextIndex;

    const table = await catalog.resolveTablePath(parsedPath.pathParts);
    refs.push({
      alias: alias.alias,
      pathParts: parsedPath.pathParts,
      table,
    });

    while (tokens[index]?.type === "comma") {
      const nextPath = parseTablePath(tokens, index + 1);
      if (nextPath == null) {
        break;
      }

      index = nextPath.nextIndex;
      const nextAlias = parseTableAlias(tokens, index);
      index = nextAlias.nextIndex;

      refs.push({
        alias: nextAlias.alias,
        pathParts: nextPath.pathParts,
        table: await catalog.resolveTablePath(nextPath.pathParts),
      });
    }
  }

  return refs;
};

const getReferenceQualifier = (ref: SqlTableReference) =>
  ref.alias ?? ref.table?.tableName ?? ref.pathParts[ref.pathParts.length - 1];

const aliasCompletionItems = (
  pathAtCursor: SqlPathAtCursor | null,
  refs: SqlTableReference[],
): SqlCatalogCompletionItem[] => {
  if (
    pathAtCursor != null &&
    (pathAtCursor.completedParts.length > 0 || pathAtCursor.trailingDot)
  ) {
    return [];
  }

  const prefix = pathAtCursor?.prefix ?? "";
  return refs
    .filter(
      (ref): ref is SqlTableReference & { alias: string } =>
        ref.alias != null && matchesPrefix(ref.alias, prefix),
    )
    .map((ref) => {
      const tablePath =
        ref.table != null ? formatFullTablePath(ref.table) : ref.pathParts.join(".");
      return {
        detail: `Alias for ${tablePath}`,
        documentation: `Table alias for ${tablePath}`,
        insertText: formatIdentifier(ref.alias),
        kind: "alias",
        label: ref.alias,
        sortText: `0_0_alias_${ref.alias}`,
      };
    });
};

const resolveQualifiedReference = async (
  qualifierParts: string[],
  refs: SqlTableReference[],
  catalog: SqlCatalogMetadataCache,
) => {
  if (qualifierParts.length === 1) {
    const qualifier = normalizeIdentifier(qualifierParts[0]);
    const matchingRef = refs.find((ref) => {
      const table = ref.table;
      return (
        normalizeIdentifier(ref.alias ?? "") === qualifier ||
        normalizeIdentifier(table?.tableName ?? ref.pathParts[ref.pathParts.length - 1] ?? "") ===
          qualifier ||
        normalizeIdentifier(ref.pathParts.join(".")) === qualifier
      );
    });

    return matchingRef?.table;
  }

  const matchingRef = refs.find(
    (ref) =>
      ref.table != null &&
      normalizeIdentifier(formatFullTablePath(ref.table)) ===
        normalizeIdentifier(qualifierParts.join(".")),
  );

  return matchingRef?.table ?? catalog.resolveTablePath(qualifierParts);
};

const columnSuggestionItems = (
  columns: SqlCatalogColumn[],
  table: SqlCatalogTable,
  options: { prefix: string; qualifier?: string; qualifiedInsert?: boolean },
): SqlCatalogCompletionItem[] =>
  columns
    .filter((column) => matchesPrefix(column.name, options.prefix))
    .map((column) => ({
      detail: `${column.type} - ${options.qualifier ?? table.tableName}`,
      documentation: `Column from ${formatFullTablePath(table)}`,
      insertText:
        options.qualifiedInsert && options.qualifier != null
          ? `${formatIdentifier(options.qualifier)}.${formatIdentifier(column.name)}`
          : formatIdentifier(column.name),
      kind: "column",
      label:
        options.qualifiedInsert && options.qualifier != null
          ? `${options.qualifier}.${column.name}`
          : column.name,
      sortText: `0_${options.qualifier ?? table.tableName}_${column.name}`,
    }));

const getQualifiedColumnCompletions = async (
  pathAtCursor: SqlPathAtCursor,
  refs: SqlTableReference[],
  catalog: SqlCatalogMetadataCache,
): Promise<SqlCatalogCompletionItem[]> => {
  if (pathAtCursor.completedParts.length === 0) {
    return [];
  }

  const table = await resolveQualifiedReference(pathAtCursor.completedParts, refs, catalog);
  if (table == null) {
    return [];
  }

  const columns = await catalog.getColumnsForTable(table);
  return columnSuggestionItems(columns, table, {
    prefix: pathAtCursor.prefix,
    qualifier: pathAtCursor.completedParts.join("."),
  });
};

const tablePathCompletionItems = async (
  pathAtCursor: SqlPathAtCursor | null,
  catalog: SqlCatalogMetadataCache,
): Promise<SqlCatalogCompletionItem[]> => {
  await catalog.ensureTableListLoaded();

  const completedParts = pathAtCursor?.completedParts ?? [];
  const prefix = pathAtCursor?.prefix ?? "";

  if (completedParts.length === 0) {
    return catalog
      .getKnownTables()
      .filter((table) => {
        const fullPath = formatFullTablePath(table);
        return matchesPrefix(fullPath, prefix) || matchesPrefix(table.tableName, prefix);
      })
      .map((table) => ({
        detail: `${table.catalogName}.${table.dbSchemaName}`,
        documentation: "Catalog table",
        insertText: formatQuotedFullTablePath(table),
        kind: "table",
        label: formatFullTablePath(table),
        sortText: `0_${formatFullTablePath(table)}`,
      }));
  }

  if (completedParts.length === 1) {
    const [catalogName] = completedParts;
    return catalog
      .getKnownSchemaNames(catalogName)
      .filter((schemaName) => matchesPrefix(schemaName, prefix))
      .map((schemaName) => ({
        detail: catalogName,
        documentation: "Catalog schema",
        insertText: formatIdentifier(schemaName),
        kind: "schema",
        label: schemaName,
        sortText: `0_${catalogName}.${schemaName}`,
      }));
  }

  if (completedParts.length === 2) {
    const [catalogName, dbSchemaName] = completedParts;
    return catalog
      .getKnownTablesInSchema(catalogName, dbSchemaName)
      .filter((table) => matchesPrefix(table.tableName, prefix))
      .map((table) => ({
        detail: `${table.catalogName}.${table.dbSchemaName}`,
        documentation: "Catalog table",
        insertText: formatIdentifier(table.tableName),
        kind: "table",
        label: table.tableName,
        sortText: `0_${formatFullTablePath(table)}`,
      }));
  }

  return [];
};

const datasourceNameCompletionItems = async (
  catalog: SqlCatalogMetadataCache,
  prefix: string,
): Promise<SqlCatalogCompletionItem[]> => {
  await catalog.ensureCatalogListLoaded();

  return catalog
    .getKnownCatalogNames()
    .filter((catalogName) => matchesPrefix(catalogName, prefix))
    .map((catalogName) => ({
      detail: "Data source",
      documentation: "Data source from the SQL catalog",
      insertText: formatIdentifier(catalogName),
      kind: "datasource",
      label: catalogName,
      sortText: `0_${catalogName}`,
    }));
};

const schemaCompletionItems = async (
  catalog: SqlCatalogMetadataCache,
  catalogName: string,
  prefix: string,
): Promise<SqlCatalogCompletionItem[]> => {
  await catalog.ensureTableListLoaded();

  return catalog
    .getKnownSchemaNames(catalogName)
    .filter((schemaName) => matchesPrefix(schemaName, prefix))
    .map((schemaName) => ({
      detail: catalogName,
      documentation: "Catalog schema",
      insertText: formatIdentifier(schemaName),
      kind: "schema",
      label: schemaName,
      sortText: `0_${catalogName}.${schemaName}`,
    }));
};

const datasourcePathCompletionItems = async (
  pathAtCursor: SqlPathAtCursor,
  catalog: SqlCatalogMetadataCache,
): Promise<SqlCatalogCompletionItem[]> => {
  const { completedParts, prefix } = pathAtCursor;

  if (completedParts.length === 0) {
    const namespaceCompletion = matchesPrefix(DATASOURCES_NAMESPACE, prefix)
      ? [
          {
            detail: "Data source namespace",
            documentation: "Browse data sources",
            insertText: DATASOURCES_NAMESPACE,
            kind: "datasource" as const,
            label: DATASOURCES_NAMESPACE,
            sortText: `0_${DATASOURCES_NAMESPACE}`,
          },
        ]
      : [];

    return [...namespaceCompletion, ...(await datasourceNameCompletionItems(catalog, prefix))];
  }

  const isNamespacedPath = isDatasourcesNamespace(completedParts[0]);
  const datasourcePathParts = isNamespacedPath ? completedParts.slice(1) : completedParts;

  if (isNamespacedPath && datasourcePathParts.length === 0) {
    return datasourceNameCompletionItems(catalog, prefix);
  }

  const catalogName = datasourcePathParts[0];
  if (catalogName == null) {
    return [];
  }

  await catalog.ensureCatalogListLoaded();
  const knownCatalogName = catalog
    .getKnownCatalogNames()
    .find((knownName) => normalizeIdentifier(knownName) === normalizeIdentifier(catalogName));
  if (knownCatalogName == null) {
    return [];
  }

  if (datasourcePathParts.length === 1) {
    return schemaCompletionItems(catalog, knownCatalogName, prefix);
  }

  await catalog.ensureTableListLoaded();

  if (datasourcePathParts.length === 2) {
    const [, dbSchemaName] = datasourcePathParts;
    return catalog
      .getKnownTablesInSchema(knownCatalogName, dbSchemaName)
      .filter((table) => matchesPrefix(table.tableName, prefix))
      .map((table) => ({
        detail: `${table.catalogName}.${table.dbSchemaName}`,
        documentation: "Catalog table",
        insertText: formatIdentifier(table.tableName),
        kind: "table",
        label: table.tableName,
        sortText: `0_${formatFullTablePath(table)}`,
      }));
  }

  if (datasourcePathParts.length === 3) {
    const [, dbSchemaName, tableName] = datasourcePathParts;
    const table = await catalog.resolveTablePath([knownCatalogName, dbSchemaName, tableName]);
    if (table == null) {
      return [];
    }

    return columnSuggestionItems(await catalog.getColumnsForTable(table), table, { prefix });
  }

  return [];
};

const shouldSuggestUnqualifiedColumns = (
  tokensBeforeCursor: SqlToken[],
  refs: SqlTableReference[],
) => {
  if (refs.length === 0) {
    return false;
  }

  for (let index = tokensBeforeCursor.length - 1; index >= 0; index -= 1) {
    const token = tokensBeforeCursor[index];
    if (token.type === "semicolon") {
      return false;
    }
    if (tokenIsIdentifierValue(token, "from") || tokenIsIdentifierValue(token, "join")) {
      return false;
    }
    if (token.type === "comma" || COLUMN_CONTEXT_KEYWORDS.has(normalizeIdentifier(token.value))) {
      return true;
    }
  }

  return false;
};

const getUnqualifiedColumnCompletions = async (
  pathAtCursor: SqlPathAtCursor | null,
  refs: SqlTableReference[],
  catalog: SqlCatalogMetadataCache,
): Promise<SqlCatalogCompletionItem[]> => {
  const prefix = pathAtCursor?.completedParts.length === 0 ? pathAtCursor.prefix : "";
  const suggestions: SqlCatalogCompletionItem[] = [];

  for (const ref of refs) {
    if (ref.table == null) {
      continue;
    }

    const columns = await catalog.getColumnsForTable(ref.table);
    const qualifier = getReferenceQualifier(ref);
    suggestions.push(
      ...columnSuggestionItems(columns, ref.table, {
        prefix,
        qualifier,
        qualifiedInsert: true,
      }),
    );
    suggestions.push(
      ...columnSuggestionItems(columns, ref.table, {
        prefix,
        qualifier,
      }).map((item) => ({ ...item, sortText: `1_${item.sortText}` })),
    );
  }

  return suggestions;
};

const keywordCompletionItems = (prefix: string): SqlCatalogCompletionItem[] =>
  SQL_KEYWORDS.filter((keyword) => matchesPrefix(keyword, prefix)).map((keyword) => ({
    insertText: keyword,
    kind: "keyword",
    label: keyword,
    sortText: `2_${keyword}`,
  }));

const dedupeCompletions = (items: SqlCatalogCompletionItem[]) =>
  Array.from(
    new Map(
      items.map((item) => [
        `${item.kind}:${item.label}:${item.insertText}:${item.detail ?? ""}`,
        item,
      ]),
    ).values(),
  );

export const getSqlCatalogCompletions = async ({
  catalog,
  cursorOffset,
  sql,
}: SqlCatalogCompletionRequest): Promise<SqlCatalogCompletionItem[]> => {
  const sqlBeforeCursor = sql.slice(0, cursorOffset);
  const tokensBeforeCursor = tokenizeSql(sqlBeforeCursor);
  const pathAtCursor = getPathAtCursor(tokensBeforeCursor, cursorOffset);
  const refs = await extractTableReferences(sql, catalog);
  const suggestions: SqlCatalogCompletionItem[] = [];
  const isQualifiedPath =
    pathAtCursor != null && (pathAtCursor.completedParts.length > 0 || pathAtCursor.trailingDot);

  if (isTablePathContext(tokensBeforeCursor, pathAtCursor)) {
    if (pathAtCursor != null && isDatasourcesNamespace(pathAtCursor.completedParts[0] ?? "")) {
      suggestions.push(...(await datasourcePathCompletionItems(pathAtCursor, catalog)));
    } else {
      suggestions.push(...(await tablePathCompletionItems(pathAtCursor, catalog)));
    }
    return dedupeCompletions(suggestions);
  }

  if (pathAtCursor != null && isQualifiedPath) {
    suggestions.push(...(await datasourcePathCompletionItems(pathAtCursor, catalog)));
    if (suggestions.length > 0) {
      return dedupeCompletions(suggestions);
    }

    if (pathAtCursor.completedParts.length > 0) {
      suggestions.push(...(await getQualifiedColumnCompletions(pathAtCursor, refs, catalog)));
      if (suggestions.length > 0) {
        return dedupeCompletions(suggestions);
      }
    }
  }

  if (shouldSuggestUnqualifiedColumns(tokensBeforeCursor, refs)) {
    suggestions.push(...aliasCompletionItems(pathAtCursor, refs));
    suggestions.push(...(await getUnqualifiedColumnCompletions(pathAtCursor, refs, catalog)));
  }

  if (pathAtCursor == null) {
    suggestions.push(...aliasCompletionItems(pathAtCursor, refs));
    suggestions.push(...(await datasourceNameCompletionItems(catalog, "")));
  } else if (!isQualifiedPath) {
    suggestions.push(...(await datasourcePathCompletionItems(pathAtCursor, catalog)));
  }

  suggestions.push(...keywordCompletionItems(pathAtCursor?.prefix ?? ""));
  return dedupeCompletions(suggestions);
};
